import React, { useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { getMenuWithInventoryAndModifierNamesSelector } from 'bento-ordering/menus/selectors';
import PageLoadSpinner from 'components/PageLoadSpinner';
import { getFilteredMenuForPromotion } from 'bento-ordering/utils/getFilteredMenuForPromotion';
import { openNotificationModal } from 'bento-ordering/base/actions/notificationModals';
import { FILTERED_MENU_ITEM_NOT_ON_CURRENT_MENU } from 'bento-ordering/cart/constants/notificationTypes';
import { useFilteredMenu } from 'hooks/useFilteredMenu';

export const MENU_NAV_BAR = 'menu_nav_bar';
export const FILTERED_ITEM_NAV_BAR = 'filtered_item_nav_bar';

export const MenuContext = React.createContext();
MenuContext.displayName = 'MenuContext';

function MenuProvider(props) {
  const dispatchModal = useDispatch();
  const locationInventoryState = useSelector(state => state.locationInventory);
  const cartState = useSelector(state => state.cart);
  const menuState = useSelector(state => state.menu);
  const menuItemsDetailState = useSelector(state => state.menuItemsDetail);
  const menu = useMemo(
    () =>
      getMenuWithInventoryAndModifierNamesSelector({
        locationInventory: locationInventoryState,
        cart: cartState,
        menu: menuState,
        menuItemsDetail: menuItemsDetailState
      }),
    [locationInventoryState, cartState, menuState, menuItemsDetailState]
  );

  const {
    setSearchTerm,
    setFilteredMenu,
    setMenuNavType,
    setFilteredPromotion,
    state
  } = useFilteredMenu(menu);

  if (menu.isFetching) {
    return <PageLoadSpinner />;
  }

  const createFilteredMenu = promotion => {
    if (promotion) {
      let results = [];
      let qualifyingItems = [];
      let productPromotionDetails = [];

      const hasQualifyingItems = !!promotion.promotion.qualifying_item_details
        .length;
      const hasProductPromotionDetails = !!promotion.promotion
        .product_promotion_details.length;

      if (hasQualifyingItems) {
        qualifyingItems = getFilteredMenuForPromotion(
          promotion.promotion.qualifying_item_details,
          menuItemsDetailState,
          promotion.promotion.qualifying_items_menu_section_title
        );
        // if has qualifying items and none are returned, menu item is not on current menu
        if (qualifyingItems.length === 0) {
          return handleMenuItemNotOnCurrentMenuError();
        }
      }

      if (hasProductPromotionDetails) {
        productPromotionDetails = getFilteredMenuForPromotion(
          promotion.promotion.product_promotion_details,
          menuItemsDetailState,
          promotion.promotion.reward_items_menu_section_title
        );
        // if has reward items and none are returned, menu item is not on current menu
        if (productPromotionDetails.length === 0) {
          return handleMenuItemNotOnCurrentMenuError();
        }
      }

      results = [...qualifyingItems, ...productPromotionDetails];

      const filteredMenu = {
        sections: results
      };

      setFilteredPromotion(promotion);
      setMenuNavType(FILTERED_ITEM_NAV_BAR);
      setFilteredMenu(filteredMenu);
    }
  };

  const handleMenuItemNotOnCurrentMenuError = () => {
    dispatchModal(
      openNotificationModal(FILTERED_MENU_ITEM_NOT_ON_CURRENT_MENU)
    );
  };

  const resetFilteredPromoId = () => {
    setMenuNavType(MENU_NAV_BAR);
    setFilteredMenu(menu);
    setFilteredPromotion(null);
  };

  return (
    <MenuContext.Provider
      value={{
        menu: state.filteredMenu,
        setFilteredMenu,
        isFilteringInProgress: state.isFilteringInProgress,
        setSearchTerm,
        searchTerm: state.searchTerm,
        menuNavType: state.menuNavType,
        resetFilteredPromoId,
        filteredPromotion: state.filteredPromotion,
        createFilteredMenu
      }}
      {...props}
    />
  );
}

function useMenu() {
  const context = React.useContext(MenuContext);
  if (context === undefined) {
    throw new Error('useMenu must be used within a MenuProvider');
  }
  return context;
}

export { MenuProvider, useMenu };
